"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, type CSSProperties } from "react";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import type { SpotlightHero } from "@/contexts/feed/domain/spotlight/spotlight-content";
import { useFeedImageUrl } from "../../model/use-feed-image";
import { SpotlightCoverHero } from "./spotlight-cover-hero";
export const INK = "text-[#091E42] dark:text-gray-50";
export const MUTED = "text-[#44546F] dark:text-gray-300";
const FRAME = "relative aspect-1200/630 w-full overflow-hidden bg-[#0b1230]/5 dark:bg-white/4";

/**
 * The area a `fit="contain"` hero sits in: 5:4 from sm up, the media centred in
 * it at its own shape; below sm it is just the media's own height, so a phone
 * gets no empty bands.
 */
export const HERO_CONTAIN_AREA = "flex items-center justify-center sm:aspect-5/4";

/**
 * A contained hero: its own shape, never cropped, rounded and ringed itself.
 * Full width unless it is taller than the 5:4 area, when it narrows until its
 * height fits: the area's height is 80% of its width, so the fitting width is
 * 80% times the media's own width/height, read once it loads.
 */
const CONTAIN = "block h-auto w-full rounded-xl ring-1 ring-black/6 sm:w-(--hero-fit-w) dark:ring-white/10";
function useContainFit() {
  const $ = _c(5);
  const [ratio, setRatio] = useState(null);
  const t0 = ratio ? `${Math.min(100, 80 * ratio)}%` : "100%";
  let t1;
  if ($[0] !== t0) {
    t1 = {
      "--hero-fit-w": t0
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const style = t1 as CSSProperties;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = (width, height) => {
      if (width > 0 && height > 0) {
        setRatio(width / height);
      }
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const measure = t2;
  let t3;
  if ($[3] !== style) {
    t3 = {
      style,
      measure
    };
    $[3] = style;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}

/**
 * How the hero takes its space. "frame" (the default) fills the feed's 1200:630
 * frame and crops to it; "contain" keeps the media's own shape for a
 * HERO_CONTAIN_AREA, and a cover fills that area.
 */
export type SpotlightHeroFit = "frame" | "contain";

/**
 * The post's picture or clip, through the feed's platform-wide media path
 * (every broker can see a super-admin's upload). A clip loops muted, the way a
 * product demo does; under reduced motion it is its poster, still.
 */
export function SpotlightHeroMedia(t0) {
  const $ = _c(17);
  const {
    hero,
    reduced,
    fit: t1,
    className
  } = t0;
  const fit = t1 === undefined ? "frame" : t1;
  if (hero.kind === "cover") {
    const fill = fit === "contain" && "rounded-xl ring-1 ring-black/6 sm:aspect-auto sm:self-stretch dark:ring-white/10";
    const t2 = hero.label;
    const t3 = hero.color;
    let t4;
    if ($[0] !== className || $[1] !== fill) {
      t4 = cn(fill, className);
      $[0] = className;
      $[1] = fill;
      $[2] = t4;
    } else {
      t4 = $[2];
    }
    let t5;
    if ($[3] !== hero.color || $[4] !== hero.label || $[5] !== t4) {
      t5 = <SpotlightCoverHero label={t2} color={t3} className={t4} />;
      $[3] = hero.color;
      $[4] = hero.label;
      $[5] = t4;
      $[6] = t5;
    } else {
      t5 = $[6];
    }
    return t5;
  }
  if (hero.kind === "video") {
    let t2;
    if ($[7] !== className || $[8] !== fit || $[9] !== hero || $[10] !== reduced) {
      t2 = <HeroVideo hero={hero} reduced={reduced} fit={fit} className={className} />;
      $[7] = className;
      $[8] = fit;
      $[9] = hero;
      $[10] = reduced;
      $[11] = t2;
    } else {
      t2 = $[11];
    }
    return t2;
  }
  let t2;
  if ($[12] !== className || $[13] !== fit || $[14] !== hero.alt || $[15] !== hero.fileUuid) {
    t2 = <HeroImage fileUuid={hero.fileUuid} alt={hero.alt} fit={fit} className={className} />;
    $[12] = className;
    $[13] = fit;
    $[14] = hero.alt;
    $[15] = hero.fileUuid;
    $[16] = t2;
  } else {
    t2 = $[16];
  }
  return t2;
}
function HeroImage(t0) {
  const $ = _c(16);
  const {
    fileUuid,
    alt,
    fit,
    className
  } = t0;
  const {
    url,
    isLoading
  } = useFeedImageUrl(fileUuid);
  const {
    style,
    measure
  } = useContainFit();
  if (fit === "contain") {
    let t1;
    if ($[0] !== alt || $[1] !== className || $[2] !== isLoading || $[3] !== measure || $[4] !== style || $[5] !== url) {
      t1 = url ? <img src={url} alt={alt} style={style} onLoad={e => measure(e.currentTarget.naturalWidth, e.currentTarget.naturalHeight)} className={cn(CONTAIN, className)} /> : isLoading ? <div className={cn(FRAME, "animate-pulse rounded-xl", className)} /> : null;
      $[0] = alt;
      $[1] = className;
      $[2] = isLoading;
      $[3] = measure;
      $[4] = style;
      $[5] = url;
      $[6] = t1;
    } else {
      t1 = $[6];
    }
    return t1;
  }
  let t1;
  if ($[7] !== className) {
    t1 = cn(FRAME, className);
    $[7] = className;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  let t2;
  if ($[9] !== alt || $[10] !== isLoading || $[11] !== url) {
    t2 = url ? <img src={url} alt={alt} className="h-full w-full object-cover" /> : isLoading ? <div className="h-full w-full animate-pulse" /> : null;
    $[9] = alt;
    $[10] = isLoading;
    $[11] = url;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  let t3;
  if ($[13] !== t1 || $[14] !== t2) {
    t3 = <div className={t1}>{t2}</div>;
    $[13] = t1;
    $[14] = t2;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  return t3;
}
function HeroVideo(t0) {
  const $ = _c(16);
  const {
    hero,
    reduced,
    fit,
    className
  } = t0;
  const {
    url: videoUrl
  } = useFeedImageUrl(reduced ? null : hero.fileUuid);
  const {
    url: posterUrl
  } = useFeedImageUrl(hero.posterFileUuid);
  const {
    style,
    measure
  } = useContainFit();
  if (fit === "contain") {
    let t1;
    if ($[0] !== className || $[1] !== measure || $[2] !== posterUrl || $[3] !== reduced || $[4] !== style || $[5] !== videoUrl) {
      t1 = reduced ? posterUrl ? <img src={posterUrl} alt="" style={style} onLoad={e => measure(e.currentTarget.naturalWidth, e.currentTarget.naturalHeight)} className={cn(CONTAIN, className)} data-testid="spotlight-poster" /> : null : videoUrl ? <video src={videoUrl} poster={posterUrl ?? undefined} muted={true} loop={true} autoPlay={true} playsInline={true} aria-hidden={true} style={style} onLoadedMetadata={e_0 => measure(e_0.currentTarget.videoWidth, e_0.currentTarget.videoHeight)} className={cn(CONTAIN, className)} /> : null;
      $[0] = className;
      $[1] = measure;
      $[2] = posterUrl;
      $[3] = reduced;
      $[4] = style;
      $[5] = videoUrl;
      $[6] = t1;
    } else {
      t1 = $[6];
    }
    return t1;
  }
  let t1;
  if ($[7] !== className) {
    t1 = cn(FRAME, className);
    $[7] = className;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  let t2;
  if ($[9] !== posterUrl || $[10] !== reduced || $[11] !== videoUrl) {
    t2 = reduced ? posterUrl ? <img src={posterUrl} alt="" className="h-full w-full object-cover" data-testid="spotlight-poster" /> : null : videoUrl ? <video src={videoUrl} poster={posterUrl ?? undefined} muted={true} loop={true} autoPlay={true} playsInline={true} aria-hidden={true} className="h-full w-full object-cover" /> : null;
    $[9] = posterUrl;
    $[10] = reduced;
    $[11] = videoUrl;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  let t3;
  if ($[13] !== t1 || $[14] !== t2) {
    t3 = <div className={t1}>{t2}</div>;
    $[13] = t1;
    $[14] = t2;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  return t3;
}

/** Category + "New on Cova", in the category's own colour, as the feed card shows it. */
export function SpotlightEyebrow(t0) {
  const $ = _c(9);
  const {
    categoryName,
    categoryColor,
    className
  } = t0;
  const color = categoryColor ?? "#5651ff";
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.08em]", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== categoryName || $[3] !== color) {
    t2 = categoryName && <span className="rounded-full px-2 py-0.5" style={{
      color,
      backgroundColor: `${color}14`
    }}>{categoryName}</span>;
    $[2] = categoryName;
    $[3] = color;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="text-gray-400 dark:text-gray-500">New on Cova</span>;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t2) {
    t4 = <div className={t1}>{t2}{t3}</div>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}
export function SpotlightPrimaryButton(t0) {
  const $ = _c(9);
  const {
    label,
    onClick,
    disabled,
    className,
    autoFocus
  } = t0;
  const t1 = autoFocus ? "" : undefined;
  let t2;
  if ($[0] !== className) {
    t2 = cn("group inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-full bg-[#5651ff] px-5 text-sm font-semibold text-white shadow-[0_4px_14px_rgba(86,81,255,0.35)] transition-all hover:bg-[#4540cc] hover:shadow-[0_6px_20px_rgba(86,81,255,0.45)] active:scale-[0.97] disabled:pointer-events-none disabled:opacity-60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#5651ff]", className);
    $[0] = className;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden={true} />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== disabled || $[4] !== label || $[5] !== onClick || $[6] !== t1 || $[7] !== t2) {
    t4 = <button type="button" onClick={onClick} disabled={disabled} data-autofocus={t1} className={t2}>{label}{t3}</button>;
    $[3] = disabled;
    $[4] = label;
    $[5] = onClick;
    $[6] = t1;
    $[7] = t2;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}
export function SpotlightQuietButton(t0) {
  const $ = _c(7);
  const {
    label,
    onClick,
    disabled,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("inline-flex h-10 items-center justify-center whitespace-nowrap rounded-full px-4 text-sm font-medium text-gray-600 transition-all hover:bg-black/4 active:scale-[0.97] disabled:pointer-events-none disabled:opacity-60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#5651ff] dark:text-gray-300 dark:hover:bg-white/6", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== disabled || $[3] !== label || $[4] !== onClick || $[5] !== t1) {
    t2 = <button type="button" onClick={onClick} disabled={disabled} className={t1}>{label}</button>;
    $[2] = disabled;
    $[3] = label;
    $[4] = onClick;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}